/**
 * useMobileCapture Hook
 *
 * Provides mobile device detection and HTML5 capture attribute support
 * for enabling camera/gallery access on mobile devices.
 *
 * @example
 * const { isMobileDevice, getCaptureAttribute, supportsCapture } = useMobileCapture()
 *
 * // In file input
 * <input
 *   type="file"
 *   accept="image/*"
 *   capture={getCaptureAttribute('image')}
 * />
 */

import { useMemo } from 'react'
import { isMobileDevice as detectMobile, supportsCapture as detectCaptureSupport } from '../utils/mobileDetection'

export interface UseMobileCaptureReturn {
  /**
   * Whether the current device is a mobile device
   */
  isMobileDevice: boolean

  /**
   * Gets the capture attribute value for the given file type
   * @param fileType File type category (image, video, audio, document)
   * @returns 'environment' for rear camera, 'user' for front camera, undefined for non-mobile
   */
  getCaptureAttribute: (fileType: string) => string | undefined

  /**
   * Whether the browser supports the HTML5 capture attribute
   */
  supportsCapture: boolean
}

/**
 * Hook for mobile camera/gallery capture support
 *
 * Detects mobile devices and provides the appropriate capture attribute
 * for HTML5 file inputs to enable native camera access.
 *
 * @returns Object with mobile detection and capture attribute helpers
 */
export function useMobileCapture(): UseMobileCaptureReturn {
  // Memoize mobile detection (doesn't change during component lifecycle)
  const isMobileDevice = useMemo(() => detectMobile(), [])

  const supportsCapture = useMemo(() => detectCaptureSupport(), [])

  /**
   * Returns the appropriate capture attribute value based on file type
   *
   * - 'environment': Rear-facing camera (default for photos)
   * - 'user': Front-facing camera (selfies)
   * - undefined: Desktop or unsupported file type
   */
  const getCaptureAttribute = (fileType: string): string | undefined => {
    // Only provide capture attribute on mobile devices with support
    if (!isMobileDevice || !supportsCapture) {
      return undefined
    }

    // Normalize file type
    const normalizedType = fileType.toLowerCase()

    // For images: use rear camera by default
    if (normalizedType === 'image' || normalizedType.startsWith('image/')) {
      return 'environment'
    }

    // For videos: use rear camera by default
    if (normalizedType === 'video' || normalizedType.startsWith('video/')) {
      return 'environment'
    }

    // For other file types (audio, document): no capture attribute
    return undefined
  }

  return {
    isMobileDevice,
    getCaptureAttribute,
    supportsCapture
  }
}
